// src/pages/MCQTest.jsx
import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { doc, getDoc, addDoc, collection, runTransaction, serverTimestamp, arrayUnion } from "firebase/firestore";
import { motion, AnimatePresence } from "framer-motion";
import {
  HiArrowLeft, HiAcademicCap, HiBookOpen, HiLightningBolt, HiClipboardList,
  HiCheckCircle, HiXCircle, HiClock, HiPlay, HiEye, HiChevronDown,
} from "react-icons/hi";
import { db } from "../components/firebase/firebase";
import Loader from "../components/common/Loader";
import { SEO } from "../components/common/SEO";
import { useAuth } from "../context/AuthContext";
import { xpForAttempt, levelForXp, nextStreak, evaluateBadges, badgeById, topicKey, isoDay } from "../lib/gamification";
import { weekKey } from "../lib/goals";
import ExamTimer from "../components/student/ExamTimer";
import SaveButton from "../components/student/SaveButton";

const BOARD_LABELS = { state: "State Board", cbse: "CBSE", competitive: "Competitive" };
const BOARD_COLORS = {
  state:       "bg-blue-50 text-blue-700 border-blue-100",
  cbse:        "bg-indigo-50 text-indigo-700 border-indigo-100",
  competitive: "bg-sky-50 text-sky-700 border-sky-100",
};

// Base styles for the MCQ HTML. Embedded answers are hidden by default; the
// `.reveal-answers` / `.review-mode` / `.mistakes-only` classes (toggled on the
// container after submit) override this WITHOUT re-rendering the innerHTML, so
// the student's selections and our inline review markers survive.
const MCQ_BASE_STYLES = `
  <style>
    .mcq-wrap * { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important; box-sizing: border-box; }
    .mcq-wrap { padding: 0; }
    .mcq-wrap p, .mcq-wrap div, .mcq-wrap li { font-size: 0.92rem; line-height: 1.65; color: #1f2937; }
    .mcq-wrap input[type="radio"] { accent-color: #2563eb; width: 16px; height: 16px; margin-right: 8px; cursor: pointer; flex-shrink: 0; }
    .mcq-wrap label { display: flex; align-items: flex-start; gap: 4px; padding: 6px 4px; cursor: pointer; border-radius: 6px; transition: background 0.15s; }
    .mcq-wrap label:hover { background: #eff6ff; }
    .mcq-wrap table { width: 100%; border-collapse: collapse; margin-bottom: 1rem; }
    .mcq-wrap td, .mcq-wrap th { padding: 6px 10px; border: 1px solid #e5e7eb; font-size: 0.88rem; }
    .mcq-wrap th { background: #eff6ff; font-weight: 600; }
    .mcq-wrap h1,.mcq-wrap h2,.mcq-wrap h3,.mcq-wrap h4 { color: #111827; margin: 1rem 0 0.5rem; font-weight: 700; }
    .mcq-wrap ol, .mcq-wrap ul { padding-left: 1.25rem; margin-bottom: 0.75rem; }
    .mcq-wrap ol > li { margin-bottom: 0.75rem; font-weight: 600; }
    .mcq-wrap .answer, .mcq-wrap [class*="answer"], .mcq-wrap [class*="correct"] { display: none !important; }
  </style>
  <div class="mcq-wrap">
`;

// Overrides applied after submit via container classes (see comment above).
// Scoped through `.mcq-wrap` so they out-specify the base hide rule that lives
// inside the rendered innerHTML (which otherwise wins on source order).
const REVIEW_STYLES = `
  .reveal-answers .mcq-wrap .answer,
  .reveal-answers .mcq-wrap [class*="answer"],
  .reveal-answers .mcq-wrap [class*="correct"] {
    display: block !important; background: #dcfce7; border-left: 3px solid #16a34a;
    padding: 4px 8px; border-radius: 4px; color: #15803d; font-weight: 600; margin-top: 4px;
  }
  .review-mode .mcq-wrap label.opt-correct { background: #dcfce7 !important; box-shadow: inset 3px 0 0 #16a34a; }
  .review-mode .mcq-wrap label.opt-correct::after { content: " ✓ correct"; color: #16a34a; font-weight: 800; font-size: 0.8rem; }
  .review-mode .mcq-wrap label.opt-wrong { background: #fee2e2 !important; box-shadow: inset 3px 0 0 #dc2626; }
  .review-mode .mcq-wrap label.opt-wrong::after { content: " ✗ your answer"; color: #dc2626; font-weight: 800; font-size: 0.8rem; }
  .review-mode .mcq-wrap li.review-q { scroll-margin-top: 80px; }
  .mistakes-only .mcq-wrap li.review-q[data-correct="true"] { display: none !important; }
`;

export default function MCQTest() {
  const { id } = useParams();
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const jumpIdxRef = useRef(0);
  const { role, user, refreshProfile } = useAuth();

  const [mcq, setMcq]                   = useState(null);
  const [loading, setLoading]           = useState(true);
  const [notFound, setNotFound]         = useState(false);
  const [started, setStarted]           = useState(false);
  const [examMode, setExamMode]         = useState(false);
  const [submitted, setSubmitted]       = useState(false);
  const [answersRevealed, setAnswersRevealed] = useState(false);
  const [score, setScore]               = useState(null);
  const [missed, setMissed]             = useState([]);
  const [mistakesOnly, setMistakesOnly] = useState(false);
  const [submitError, setSubmitError]   = useState("");
  const [reward, setReward]             = useState(null);   // { xpEarned, newBadges, leveledUp, newLevel, streak }
  const [saveStatus, setSaveStatus]     = useState("idle"); // idle | saving | saved | guest | error

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, "mcqs", id));
        if (!snap.exists()) { setNotFound(true); return; }
        setMcq({ id: snap.id, ...snap.data() });
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [id]);

  const examDurationMins = mcq ? (Number(mcq.durationMins) || Math.max(5, Number(mcq.questionCount) || 10)) : 10;

  // Walk the rendered radio groups, mark correct/wrong options in place, and tag
  // each question's <li> so "mistakes only" and "jump to mistake" can target it.
  const annotateReview = (groups, answers) => {
    const root = containerRef.current;
    if (!root) return;
    Object.keys(groups).forEach((name, idx) => {
      const radios = groups[name];
      const correctAnswer = answers[idx];
      const selected = radios.find((r) => r.checked);
      const selectedValue = selected ? selected.value.trim().toUpperCase() : null;
      const isCorrect = !!(selectedValue && correctAnswer && selectedValue === correctAnswer);

      const li = (selected || radios[0])?.closest("li");
      if (li) {
        li.classList.add("review-q");
        li.setAttribute("data-q", String(idx + 1));
        li.setAttribute("data-correct", isCorrect ? "true" : "false");
      }
      radios.forEach((r) => {
        r.disabled = true;
        const label = r.closest("label") || r.parentElement;
        if (!label) return;
        const val = (r.value || "").trim().toUpperCase();
        if (correctAnswer && val === correctAnswer) label.classList.add("opt-correct");
        if (selected && r === selected && !isCorrect) label.classList.add("opt-wrong");
      });
    });
  };

  const handleSubmit = () => {
    if (submitted || !containerRef.current) return;

    const hasAnswerKey = (mcq.answerKey || "").trim().length > 0;

    // No answer key — just reveal the embedded answers in the HTML.
    if (!hasAnswerKey) {
      containerRef.current.classList.add("reveal-answers");
      setAnswersRevealed(true);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const answers = mcq.answerKey.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean);

    const radios = containerRef.current.querySelectorAll('input[type="radio"]');
    const groups = {};
    radios.forEach((r) => {
      const name = r.name || r.getAttribute("name") || "";
      if (!groups[name]) groups[name] = [];
      groups[name].push(r);
    });

    const groupNames = Object.keys(groups);
    if (groupNames.length === 0) {
      setSubmitError("No radio buttons found in the test HTML. Please check the HTML format.");
      return;
    }

    let correct = 0;
    const missedList = [];
    groupNames.forEach((name, idx) => {
      const selected = groups[name].find((r) => r.checked);
      const correctAnswer = answers[idx];
      const selectedValue = selected ? selected.value.trim().toUpperCase() : null;
      if (selectedValue && correctAnswer && selectedValue === correctAnswer) {
        correct++;
      } else {
        missedList.push({ q: idx + 1, selected: selectedValue || "Not answered", correct: correctAnswer || "?" });
      }
    });

    // Annotate the questions in place, then reveal answers + review styling.
    annotateReview(groups, answers);
    containerRef.current.classList.add("reveal-answers", "review-mode");

    setScore({ correct, total: groupNames.length });
    setMissed(missedList);
    setAnswersRevealed(true);
    setSubmitted(true);
    window.scrollTo({ top: 0, behavior: "smooth" });

    saveResult(correct, groupNames.length, missedList);
  };

  // Saves the attempt and updates XP / level / streak / badges / weekly XP / topic stats.
  const saveResult = async (correct, total, missedList) => {
    if (role !== "student" || !user) { setSaveStatus("guest"); return; }
    setSaveStatus("saving");
    const pct = total ? Math.round((correct / total) * 100) : 0;
    const xpEarned = xpForAttempt({ correct, pct });
    const tKey = topicKey(mcq.topic || "untitled");
    const subject = mcq.subject || null;
    const today = isoDay();
    const wk = weekKey();

    const attemptData = {
      mcqId: mcq.id,
      refId: mcq.refId || null,
      topic: mcq.topic || "",
      board: mcq.board || null,
      grade: mcq.grade || null,
      subject,
      total,
      correct,
      pct,
      missed: missedList,
      xpEarned,
      mode: examMode ? "exam" : "practice",
      takenAt: serverTimestamp(),
    };

    try {
      await addDoc(collection(db, "students", user.uid, "attempts"), attemptData);

      const profileRef = doc(db, "students", user.uid);
      const topicRef = doc(db, "students", user.uid, "topicStats", tKey);

      const result = await runTransaction(db, async (tx) => {
        const pSnap = await tx.get(profileRef);
        const tSnap = await tx.get(topicRef);
        const p = pSnap.exists() ? pSnap.data() : {};
        const t = tSnap.exists() ? tSnap.data() : {};

        const prevXp = p.xp || 0;
        const newXp = prevXp + xpEarned;
        const prevLevel = p.level || levelForXp(prevXp) || 1;
        const newLevel = levelForXp(newXp);
        const streak = nextStreak(p.streak || {}, today);
        const totalAttempts = (p.totalAttempts || 0) + 1;
        const newWeeklyXp = (p.weekKey === wk ? (p.weeklyXp || 0) : 0) + xpEarned;

        const tAttempts = (t.attempts || 0) + 1;
        const tCorrect = (t.totalCorrect || 0) + correct;
        const tQuestions = (t.totalQuestions || 0) + total;
        const updatedTopicStat = {
          topic: mcq.topic || "",
          subject,
          board: mcq.board || null,
          grade: mcq.grade || null,
          attempts: tAttempts,
          totalCorrect: tCorrect,
          totalQuestions: tQuestions,
          accuracy: tQuestions ? tCorrect / tQuestions : 0,
          lastAttemptAt: serverTimestamp(),
        };

        const updatedProfileSnapshot = { ...p, xp: newXp, level: newLevel, streak, totalAttempts };
        const newBadges = evaluateBadges({
          profile: updatedProfileSnapshot,
          attempt: attemptData,
          topicStat: updatedTopicStat,
        });

        tx.set(topicRef, updatedTopicStat, { merge: true });
        tx.set(
          profileRef,
          {
            xp: newXp,
            level: newLevel,
            streak,
            totalAttempts,
            weeklyXp: newWeeklyXp,
            weekKey: wk,
            ...(newBadges.length ? { badges: arrayUnion(...newBadges) } : {}),
          },
          { merge: true }
        );

        return { newLevel, leveledUp: newLevel > prevLevel, newBadges, streak };
      });

      setReward({
        xpEarned,
        newBadges: result.newBadges,
        leveledUp: result.leveledUp,
        newLevel: result.newLevel,
        streak: result.streak.current,
      });
      setSaveStatus("saved");
      refreshProfile();
    } catch (e) {
      console.error("Failed to save result", e);
      setSaveStatus("error");
    }
  };

  const resetTest = () => {
    // Clear our imperative annotations so a retake starts clean.
    const root = containerRef.current;
    if (root) {
      root.classList.remove("reveal-answers", "review-mode", "mistakes-only");
      root.querySelectorAll("input[type=radio]").forEach((r) => { r.disabled = false; r.checked = false; });
      root.querySelectorAll("label.opt-correct, label.opt-wrong").forEach((l) => l.classList.remove("opt-correct", "opt-wrong"));
      root.querySelectorAll("li.review-q").forEach((li) => { li.classList.remove("review-q"); li.removeAttribute("data-correct"); li.removeAttribute("data-q"); });
    }
    jumpIdxRef.current = 0;
    setSubmitted(false);
    setScore(null);
    setMissed([]);
    setMistakesOnly(false);
    setAnswersRevealed(false);
    setReward(null);
    setSaveStatus("idle");
    window.scrollTo(0, 0);
  };

  const toggleMistakesOnly = () => {
    const root = containerRef.current;
    if (!root) return;
    const next = !mistakesOnly;
    root.classList.toggle("mistakes-only", next);
    setMistakesOnly(next);
  };

  const jumpToNextMistake = () => {
    const root = containerRef.current;
    if (!root) return;
    const wrong = root.querySelectorAll('li.review-q[data-correct="false"]');
    if (!wrong.length) return;
    const i = jumpIdxRef.current % wrong.length;
    wrong[i].scrollIntoView({ behavior: "smooth", block: "center" });
    jumpIdxRef.current = i + 1;
  };

  if (loading) return <Loader />;

  if (notFound || !mcq) return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4 bg-blue-50">
      <HiClipboardList className="text-7xl text-blue-200 mx-auto mb-4" />
      <h2 className="text-2xl font-bold text-gray-800 mb-2">Test not found</h2>
      <p className="text-gray-400 text-sm mb-6">This MCQ test may have been removed.</p>
      <button onClick={() => navigate("/mcq")}
        className="inline-flex items-center gap-2 bg-blue-600 text-white font-semibold px-6 py-2.5 rounded-xl hover:bg-blue-700 transition-colors">
        <HiArrowLeft /> Browse Tests
      </button>
    </div>
  );

  const pct = score ? Math.round((score.correct / score.total) * 100) : 0;
  const pass = pct >= 50;
  const boardStyle = BOARD_COLORS[mcq.board] || "bg-gray-50 text-gray-600 border-gray-100";
  const styledHtml = MCQ_BASE_STYLES + (mcq.htmlContent || "") + "</div>";

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title={`${mcq.topic || "MCQ Test"} | Class ${mcq.grade} | Ravi TestPapers`}
        description={`Take the MCQ test: ${mcq.topic}. Class ${mcq.grade} — ${BOARD_LABELS[mcq.board] || mcq.board}. Free on Ravi TestPapers.`}
        canonical={`/mcq/${id}`}
      />
      {/* Review styling (applies only when container has the relevant classes). */}
      <style dangerouslySetInnerHTML={{ __html: REVIEW_STYLES }} />

      {/* Sticky topbar */}
      <div className="bg-white border-b border-blue-100 sticky top-0 z-20 shadow-sm">
        <div className="max-w-screen-lg mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <button onClick={() => navigate("/mcq")}
            className="flex-shrink-0 inline-flex items-center gap-1.5 text-gray-600 hover:text-blue-600 font-semibold text-sm transition-colors">
            <HiArrowLeft /> Back
          </button>
          <div className="flex-1 min-w-0 px-2">
            <h1 className="font-bold text-gray-900 text-sm sm:text-base truncate">{mcq.topic || "MCQ Test"}</h1>
            {mcq.refId && <p className="text-[10px] font-mono text-gray-400">#{mcq.refId}</p>}
          </div>
          {started && examMode && !submitted && (
            <ExamTimer durationMins={examDurationMins} paused={submitted} onExpire={handleSubmit} />
          )}
          <SaveButton kind="test" item={mcq} />
          {started && !submitted && (
            <button onClick={handleSubmit}
              className="flex-shrink-0 bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm px-4 py-2 rounded-xl transition-colors shadow-md shadow-blue-200">
              Submit
            </button>
          )}
        </div>
      </div>

      <div className="max-w-screen-lg mx-auto px-4 sm:px-6 py-6">

        {/* Meta chips */}
        <div className="flex flex-wrap items-center gap-2 mb-5">
          {mcq.board && (
            <span className={`inline-flex items-center gap-1.5 border text-xs font-semibold px-3 py-1.5 rounded-full ${boardStyle}`}>
              {mcq.board === "state" ? <HiAcademicCap /> : mcq.board === "cbse" ? <HiBookOpen /> : <HiLightningBolt />}
              {BOARD_LABELS[mcq.board] || mcq.board}
            </span>
          )}
          {mcq.board === "competitive" && mcq.examType && (
            <span className="inline-flex items-center bg-sky-100 text-sky-700 border border-sky-200 text-xs font-bold px-3 py-1.5 rounded-full">
              {mcq.examType}
            </span>
          )}
          {mcq.grade && (
            <span className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 border border-blue-100 text-xs font-semibold px-3 py-1.5 rounded-full">
              <HiAcademicCap /> Class {mcq.grade}
            </span>
          )}
          {mcq.questionCount > 0 && (
            <span className="inline-flex items-center bg-indigo-100 text-indigo-700 text-xs font-bold px-3 py-1.5 rounded-full">
              {mcq.questionCount} Questions
            </span>
          )}
          {examMode && started && (
            <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 text-xs font-bold px-3 py-1.5 rounded-full">
              <HiClock /> Exam mode
            </span>
          )}
        </div>

        {/* ── START GATE: choose practice vs exam mode ── */}
        {!started && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl border border-blue-100 shadow-sm p-6 sm:p-8 text-center max-w-xl mx-auto"
          >
            <div className="w-14 h-14 rounded-2xl bg-blue-100 text-blue-600 flex items-center justify-center text-2xl mx-auto mb-4">
              <HiClipboardList />
            </div>
            <h2 className="text-xl font-black text-gray-800 mb-1">{mcq.topic || "MCQ Test"}</h2>
            <p className="text-sm text-gray-400 mb-6">
              {mcq.questionCount > 0 ? `${mcq.questionCount} questions` : "Multiple choice"} · How do you want to take it?
            </p>
            <div className="grid sm:grid-cols-2 gap-3 text-left">
              <button
                onClick={() => { setExamMode(false); setStarted(true); }}
                className="group rounded-2xl border-2 border-emerald-200 bg-emerald-50 hover:border-emerald-400 p-4 transition-colors"
              >
                <div className="flex items-center gap-2 mb-1">
                  <HiPlay className="text-emerald-600 text-lg" />
                  <span className="font-extrabold text-emerald-800">Practice</span>
                </div>
                <p className="text-xs text-emerald-700">No timer. Take your time and learn at your own pace.</p>
              </button>
              <button
                onClick={() => { setExamMode(true); setStarted(true); }}
                className="group rounded-2xl border-2 border-amber-200 bg-amber-50 hover:border-amber-400 p-4 transition-colors"
              >
                <div className="flex items-center gap-2 mb-1">
                  <HiClock className="text-amber-600 text-lg" />
                  <span className="font-extrabold text-amber-800">Exam mode</span>
                </div>
                <p className="text-xs text-amber-700">{examDurationMins} min timer, auto-submits at zero. Simulate the real thing.</p>
              </button>
            </div>
          </motion.div>
        )}

        {/* Results panel — scored (with answer key) */}
        <AnimatePresence>
          {submitted && score && (
            <motion.div
              initial={{ opacity: 0, y: -16 }}
              animate={{ opacity: 1, y: 0 }}
              className={`rounded-2xl p-6 mb-6 shadow-lg ${pass ? "bg-green-500" : "bg-red-500"} text-white`}
            >
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  {pass ? <HiCheckCircle className="text-4xl text-white" /> : <HiXCircle className="text-4xl text-white" />}
                </div>
                <div className="flex-1">
                  <p className="text-white/80 text-sm font-semibold uppercase tracking-wide mb-1">
                    {pass ? "Great job!" : "Keep practising!"}
                  </p>
                  <p className="text-4xl font-black leading-none">
                    {score.correct} <span className="text-2xl font-bold text-white/70">/ {score.total}</span>
                  </p>
                  <p className="text-white/80 text-sm mt-1">{pct}% correct</p>
                </div>
                <button
                  onClick={resetTest}
                  className="flex-shrink-0 bg-white/20 hover:bg-white/30 text-white font-semibold px-4 py-2 rounded-xl text-sm transition-colors"
                >
                  Try Again
                </button>
              </div>

              {/* Review controls */}
              {missed.length > 0 && (
                <div className="mt-5 pt-4 border-t border-white/20 flex flex-wrap items-center gap-2">
                  <p className="font-bold text-sm mr-1">{missed.length} to review:</p>
                  <button
                    onClick={jumpToNextMistake}
                    className="inline-flex items-center gap-1.5 bg-white/20 hover:bg-white/30 text-white text-xs font-bold px-3 py-1.5 rounded-lg transition-colors"
                  >
                    <HiChevronDown /> Jump to next mistake
                  </button>
                  <button
                    onClick={toggleMistakesOnly}
                    className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg transition-colors ${
                      mistakesOnly ? "bg-white text-gray-800" : "bg-white/20 hover:bg-white/30 text-white"
                    }`}
                  >
                    <HiEye /> {mistakesOnly ? "Show all questions" : "Review mistakes only"}
                  </button>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Gamification rewards — only after a scored, saved attempt */}
        <AnimatePresence>
          {submitted && score && saveStatus !== "guest" && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-2xl p-5 mb-6 bg-white border border-indigo-100 shadow-sm"
            >
              {saveStatus === "saving" && <p className="text-sm text-gray-500">Saving your progress…</p>}
              {saveStatus === "error" && (
                <p className="text-sm text-red-600">Couldn't save your progress. Your score above is still correct.</p>
              )}
              {saveStatus === "saved" && reward && (
                <div className="flex flex-col gap-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="inline-flex items-center gap-1.5 bg-indigo-600 text-white text-sm font-bold px-3 py-1.5 rounded-full">
                      +{reward.xpEarned} XP
                    </span>
                    {reward.streak > 0 && (
                      <span className="inline-flex items-center gap-1.5 bg-orange-100 text-orange-700 text-sm font-bold px-3 py-1.5 rounded-full">
                        🔥 {reward.streak}-day streak
                      </span>
                    )}
                    {reward.leveledUp && (
                      <span className="inline-flex items-center gap-1.5 bg-amber-100 text-amber-700 text-sm font-bold px-3 py-1.5 rounded-full">
                        ⭐ Level {reward.newLevel}!
                      </span>
                    )}
                    <Link to="/dashboard" className="ml-auto text-indigo-600 text-sm font-semibold hover:underline">
                      View dashboard →
                    </Link>
                  </div>

                  {reward.leveledUp && (
                    <div className="pt-3 border-t border-gray-100">
                      <Link
                        to={`/certificate?type=level&value=${reward.newLevel}`}
                        className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-400 to-orange-400 text-white text-sm font-bold px-4 py-2 rounded-xl hover:from-amber-500 hover:to-orange-500 transition-colors"
                      >
                        🏆 Get your Level {reward.newLevel} certificate
                      </Link>
                    </div>
                  )}

                  {reward.newBadges.length > 0 && (
                    <div className="pt-3 border-t border-gray-100">
                      <p className="text-xs font-bold text-gray-500 mb-2">New badge{reward.newBadges.length > 1 ? "s" : ""} unlocked!</p>
                      <div className="flex flex-wrap gap-2">
                        {reward.newBadges.map((bid) => {
                          const b = badgeById(bid);
                          if (!b) return null;
                          return (
                            <span key={bid} className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 text-xs font-semibold px-3 py-1.5 rounded-lg">
                              <span className="text-base">{b.icon}</span> {b.name}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Sign-in nudge for guests after a scored attempt */}
        {submitted && score && saveStatus === "guest" && (
          <div className="rounded-2xl p-4 mb-6 bg-indigo-50 border border-indigo-100 text-sm text-indigo-800 flex flex-wrap items-center gap-2">
            <span>Want to track your progress, earn XP and find your weak topics?</span>
            <Link to="/signin" className="font-bold underline">Log in as a student</Link>
          </div>
        )}

        {/* Results panel — answers revealed (no answer key) */}
        <AnimatePresence>
          {submitted && !score && answersRevealed && (
            <motion.div
              initial={{ opacity: 0, y: -16 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-2xl p-5 mb-6 bg-blue-600 text-white shadow-lg"
            >
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <HiCheckCircle className="text-3xl text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-bold text-lg leading-tight">Answers revealed below!</p>
                  <p className="text-blue-100 text-sm mt-0.5">Scroll down to review the correct answers highlighted in green.</p>
                </div>
                <button
                  onClick={resetTest}
                  className="flex-shrink-0 bg-white/20 hover:bg-white/30 text-white font-semibold px-4 py-2 rounded-xl text-sm transition-colors"
                >
                  Try Again
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Submit error */}
        {submitError && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 mb-5">
            {submitError}
          </div>
        )}

        {/* MCQ HTML content — hidden until started, kept mounted afterwards */}
        <div className={started ? "bg-white rounded-2xl border border-blue-100 shadow-sm p-5 sm:p-8" : "hidden"}>
          <div ref={containerRef} dangerouslySetInnerHTML={{ __html: styledHtml }} />
        </div>

        {/* Bottom submit */}
        {started && !submitted && (
          <div className="mt-6 text-center">
            <button
              onClick={handleSubmit}
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-black px-10 py-3.5 rounded-2xl text-base transition-colors shadow-lg shadow-blue-300"
            >
              <HiClipboardList /> Submit Test & See Results
            </button>
            <p className="text-gray-400 text-xs mt-2">Make sure you've answered all questions before submitting.</p>
          </div>
        )}
      </div>
    </div>
  );
}
